/**
 * Stable, non-reversible pseudonymization for identifier / PII fields. We use a
 * synchronous FNV-1a hash (adequate for de-duplication + join-on-device analytics
 * without shipping raw identifiers). It is intentionally NOT a security primitive —
 * it exists so no raw email/phone/id ever leaves the client.
 */

/** Field-name test: matches ids, emails, phones and session-ish keys. */
const SENSITIVE_KEY = /(^|_)id$|id$|email|e-mail|phone|tel|session|correlation|user/i;

/**
 * Compute a stable, non-reversible FNV-1a digest of `input`.
 *
 * @param input Value to hash.
 * @returns An 8-hex-digit token prefixed with `px_` (not a security primitive).
 */
export function hashValue(input: string): string {
	let hash = 0x811c9dc5;
	for (let i = 0; i < input.length; i++) {
		hash ^= input.charCodeAt(i);
		// 32-bit FNV prime multiply, kept in 32-bit range.
		hash = Math.imul(hash, 0x01000193) >>> 0;
	}
	return `px_${hash.toString(16).padStart(8, "0")}`;
}

/**
 * Whether a field name looks like an identifier or PII (id, email, phone, session…)
 * and should therefore be hashed by {@link pseudonymize}.
 */
export function isSensitiveKey(key: string): boolean {
	return SENSITIVE_KEY.test(key);
}

/**
 * Stringify an arbitrary value for hashing. Strings pass through; other
 * primitives use their own `toString`; anything else is JSON-encoded. Written
 * to satisfy `no-base-to-string` (never stringifies a bare object) without an
 * unnecessary type assertion.
 */
function scalarToString(value: unknown): string {
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
		return String(value);
	}
	return JSON.stringify(value) ?? "";
}

/**
 * Return a shallow copy of `payload` with sensitive scalar fields hashed. Nested
 * objects are recursed one level; arrays of scalars have their sensitive parent
 * key hashed element-wise.
 */
export function pseudonymize(payload: Record<string, unknown>): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(payload)) {
		if (value == null) {
			out[key] = value;
			continue;
		}
		if (isSensitiveKey(key)) {
			if (Array.isArray(value)) {
				out[key] = value.map((v) => hashValue(scalarToString(v)));
			} else if (typeof value === "object") {
				out[key] = pseudonymize(value as Record<string, unknown>);
			} else {
				out[key] = hashValue(scalarToString(value));
			}
		} else if (typeof value === "object" && !Array.isArray(value)) {
			out[key] = pseudonymize(value as Record<string, unknown>);
		} else {
			out[key] = value;
		}
	}
	return out;
}
